import { useEffect, useMemo, useState, useCallback, ChangeEvent, useLayoutEffect } from 'react';
import * as anchor from '@project-serum/anchor';
import { BackToTop } from "material-ui-back-to-top";
import axios from 'axios';
import compareAsc from 'date-fns/compareAsc';
import isSameMonth from 'date-fns/isSameMonth';

// COMPONENTS
import Header from './components/Header';
import Footer from './components/Footer';
import LinearIndeterminate from './components/LinearProgress';

// MUI
import styled from 'styled-components';
import { Container, Typography } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

import { Connection, programs } from '@metaplex/js'; 
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletDialogButton } from '@solana/wallet-adapter-material-ui';

import { useArchiveStyles } from './styles/archives';
import { VideoList } from './components/videoList';

const ConnectButton = styled(WalletDialogButton)`
  width: 100%;
  height: 60px;
  margin-top: 10px;
  margin-bottom: 5px;
  background: linear-gradient(180deg, #604ae5 0%, #813eee 100%);
  color: white;
  font-size: 16px;
  font-weight: bold;
`;

export interface HomeProps {
  candyMachineId?: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  txTimeout: number;
  rpcHost: string;
}

interface Video {
  title: string;
  url: string;
  date: string;
}

const Collections = (props: HomeProps) => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  });

  const classes = useArchiveStyles();
  const [videos, setVideos] = useState<Video[]>([]);
  const [hasGatedMint, setHasGatedMint] = useState<string>('false');
  let [filter, setFilter] = useState<string>('');
  let [filteredData, setFilteredData] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const wallet = useWallet();
  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }

    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    } as anchor.Wallet;
  }, [wallet]);

  const fetchMintedTokens = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get('https://lasvegasvp.com/api/get-current-minted.php');
      const data = await result.data;
      setIsLoading(false);
      return data;
    } catch(error) {
      console.log('error');

      return [];
    }
  };

  const refreshCandyMachineState = useCallback(async () => {
    if (!anchorWallet) {
      return;
    }

    if (hasGatedMint === 'false' && anchorWallet) {
      try {
        const { metadata: { Metadata } } = programs;
        const connection = window.location.host === 'nft.lasvegasvp.com' ? new Connection('mainnet-beta') : new Connection('devnet');
        const nftsmetadata = await Metadata.findDataByOwner(connection, anchorWallet.publicKey);
        const currentMinted = await fetchMintedTokens();

        nftsmetadata.forEach((elm) => {
          const result = currentMinted.find((curr: string) => curr === elm.mint);

          if (result) {
            setHasGatedMint('true');
            return;
          }
        });
      } catch (e) {
        console.log('error', e);
      }
    }
  }, [anchorWallet, hasGatedMint]);

  const handleChange = (event: ChangeEvent<{ value: any }>) => {
    setFilter(event.target.value as string);

    if (event.target.value === 'ALL') {
      setFilteredData(videos);
    } else {
      let newCollection: Video[] = [];

      videos.forEach(vid => {
        if (isSameMonth(new Date(event.target.value), new Date(vid.date))) {
          newCollection.push(vid);
        }
      });

      setFilteredData(newCollection);
    }
  };

  const getVideoArchives = async () => {
    const baseURL = 'https://lasvegasvp.com/api/get-video-archives.php';
    const result = await axios.get(baseURL);

    if (result) {
      let videos = result.data.sort((a: Video, b: Video) => {
        return compareAsc(new Date(a.date), new Date(b.date));
      });

      setVideos(result.data);
      setFilteredData(videos);
    }
  };

  useEffect(() => {
    if (hasGatedMint === 'true') {
      getVideoArchives();
    }
  }, [hasGatedMint]);

  useEffect(() => {
    refreshCandyMachineState();
  }, [
    anchorWallet,
    props.candyMachineId,
    props.connection,
    refreshCandyMachineState,
  ]);

  return (
    <div className={classes.root}>
        <Header />
        <Container>
          {/* main content goes here */}
          
          <Paper elevation={0} className={classes.bodyPaper}>
            <Container style={{ padding: '30px 22px', textAlign: 'left' }}>
              <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 0 }}>
                Video Archive
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={12}>
                  {isLoading &&
                    <LinearIndeterminate />
                  }
                  {hasGatedMint === 'false' && !wallet.connected ? (
                    <div>
                      <Typography
                        variant="caption"
                        align="center"
                        display="block"
                        style={{ marginTop: 7, color: 'white' }}
                      >
                        <b style={{ fontSize: '1.5rem' }}>Redeem Rewards</b>
                      </Typography>
                      <ConnectButton>Connect Wallet</ConnectButton>
                    </div>
                  ) : (
                    <>
                      {hasGatedMint === 'true' ? (
                        <>
                          <Grid item xs={12}>
                            <FormControl className={classes.formControl}>
                              <InputLabel id="demo-simple-select-helper-label" style={{ color: '#000' }}>All</InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={filter}
                                onChange={handleChange}
                                style={{ color: '#999' }}
                              >
                                <MenuItem value={'ALL'}>ALL</MenuItem>
                                <MenuItem value={'6/1/2022'}>June 2022</MenuItem>
                                <MenuItem value={'5/1/2022'}>May 2022</MenuItem>
                                <MenuItem value={'4/1/2022'}>April 2022</MenuItem>
                                <MenuItem value={'3/1/2022'}>March 2022</MenuItem>
                                <MenuItem value={'2/1/2022'}>February 2022</MenuItem>
                                <MenuItem value={'1/1/2022'}>January 2022</MenuItem>
                                <MenuItem value={'12/1/2021'}>December 2021</MenuItem>
                                <MenuItem value={'11/1/2021'}>November 2021</MenuItem>
                                <MenuItem value={'10/1/2021'}>October 2021</MenuItem>
                                <MenuItem value={'9/1/2021'}>September 2021</MenuItem>
                                <MenuItem value={'8/1/2021'}>August 2021</MenuItem>
                                <MenuItem value={'7/1/2021'}>July 2021</MenuItem>
                              </Select>
                              <FormHelperText style={{ color: '#000' }}>Select to filter videos by date</FormHelperText>
                            </FormControl>
                          </Grid>

                          {filteredData &&
                            <VideoList 
                              videos={filteredData}
                            />
                          }
                        
                        </>
                      ) : (
                        <div style={{ paddingBottom: 10, textAlign: 'center' }}>
                          <Typography variant="h5" style={{ fontWeight: 'bold' }}>You don't own any VP Bunny NFTs to access rewards.</Typography>
                        </div>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
              {/* END OF BODY CONTAINER, DON'T ADD STUFF UNDER HERE */}
            </Container>
          </Paper>
        </Container>
        <Footer />
        <BackToTop />
    </div>
  );
};

export default Collections;